// ===============================
// Validation Constants
// ===============================

export const VALIDATION_LIMITS = {
  NAME: {
    MIN: 1,
    MAX: 100,
  },
  BUSINESS_NAME: {
    MIN: 3,
    MAX: 100,
  },

  TITLE: {
    MIN: 1,
    MAX: 100,
  },

  DESCRIPTION: {
    MIN: 1,
    MAX: 5000,
  },

  ADDRESS: {
    MIN: 1,
    MAX: 255,
  },

  LOCATION: {
    MIN: 1,
    MAX: 100,
  },

  ZIP_CODE: {
    MAX: 20,
  },

  NOTES: {
    MAX: 1000,
  },

  VAT_NUMBER: {
    MAX: 11,
  },

  BANK_NAME: {
    MAX: 255,
  },

  ACCOUNT_NAME: {
    MAX: 255,
  },

  ITEM_NAME: {
    MAX: 100,
  },

  ACCOUNT_NUMBER: {
    MAX: 10,
  },

  SORT_CODE: {
    MAX: 10,
  },

  PHONE_DIGITS: 10,

  PASSWORD: {
    MIN: 8,
    MAX: 25,
  },

  BILLING_PREFERENCES: {
    VAT_RATE_MAX: 3,
    QUOTE_EXPIRY_MAX: 3,
    PAYMENT_TERMS_MAX: 3,
  },

  ORDER: {
    MAX: 10000,
  },

  COST: {
    MAX: 1000000,
  },

  QUOTE_TNC_MAX: 1000,
  FOOTER_MESSAGE_MAX: 1000,
};

// ===============================
// Validation Messages
// ===============================
const capitalize = (text?: string) =>
  text ? text.charAt(0).toUpperCase() + text.slice(1) : "";

export const VALIDATION_MESSAGES = {
  REQUIRED: (field: string) => `Please enter ${field?.toLowerCase()}`,
  SELECT_REQUIRED: (field: string) => `Please select ${field?.toLowerCase()}`,
  MIN_LENGTH: (field: string, min: number) =>
    `${capitalize(field)} must be at least ${min} characters`,
  MAX_LENGTH: (field: string, max: number) =>
    `${capitalize(field)} must be at most ${max} characters`,
  AT_LEAST_ONE_LETTER: (field: string) =>
    `${capitalize(field)} must contain at least one letter`,
  INVALID_EMAIL: "Please enter a valid email address",
  INVALID_PHONE: "Please enter a valid 10 digit phone number",
};

// ===============================
// Regex Patterns (minimal & safe)
// ===============================

export const VALIDATION_PATTERNS = {
  HAS_LETTER: /[a-zA-Z]/,
  ZIP_CODE: /^[A-Za-z0-9\s]+$/,
  PHONE: /^\(\+44\)\s\d{10}$/,
  VAT_NUMBER: /^[A-Za-z0-9]+$/,
  LOCATION: /^[A-Za-z\s]+$/,
};

// ===============================
// Helper Validators
// ===============================

// ---- Name / Title / Business Name ----
export const createNameValidation = (fieldName: string) => ({
  test: (value?: string) => {
    if (!value) return false;
    return VALIDATION_PATTERNS.HAS_LETTER.test(value);
  },
  message: VALIDATION_MESSAGES.AT_LEAST_ONE_LETTER(fieldName),
});

// ---- Description (Required) ----
export const createDescriptionValidation = (fieldName: string) => ({
  test: (value?: string) => {
    if (!value) return false;
    return VALIDATION_PATTERNS.HAS_LETTER.test(value);
  },
  message: VALIDATION_MESSAGES.AT_LEAST_ONE_LETTER(fieldName),
});

// ---- Address ----
export const createAddressValidation = (fieldName: string) => ({
  message: VALIDATION_MESSAGES.AT_LEAST_ONE_LETTER(fieldName),
  test: (value?: string) => {
    if (!value) return false;
    return VALIDATION_PATTERNS.HAS_LETTER.test(value);
  },
});

// ---- Location (City / State / Country) ----
export const createLocationValidation = (fieldName: string) => ({
  message: `${fieldName} must contain letters only`,
  test: (value?: string) => {
    if (!value) return false;
    return VALIDATION_PATTERNS.LOCATION.test(value);
  },
});

// ---- Optional Description / Notes ----
export const createOptionalDescriptionValidation = (fieldName: string) => ({
  message: VALIDATION_MESSAGES.AT_LEAST_ONE_LETTER(fieldName),
  test: (value?: string) => {
    const strValue = String(value ?? "").trim();
    if (!strValue) return true;
    return VALIDATION_PATTERNS.HAS_LETTER.test(strValue);
  },
});
